import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Zap, Building2, Bell, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";

const FEATURES = [
  {
    icon: Mic,
    title: "Real Interview Simulation",
    desc: "Talk to an AI interviewer that adapts in real-time to your answers.",
    accent: "#F59E0B",
  },
  {
    icon: Zap,
    title: "Instant AI Feedback",
    desc: "Get scored on accuracy, confidence, and code quality after every answer.",
    accent: "#00E676",
  },
  {
    icon: Building2,
    title: "Company-Specific Questions",
    desc: "Curated question banks from Google, Amazon, Microsoft and more.",
    accent: "#00E676",
  },
];

const GridLines = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[20, 40, 60, 80].map((pct) => (
      <motion.div
        key={pct}
        className="absolute left-0 right-0 h-px bg-[#F59E0B]/5"
        style={{ top: `${pct}%` }}
        animate={{ opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 4 + pct / 20, repeat: Infinity, delay: pct / 40 }}
      />
    ))}
    {[15, 30, 50, 70, 85].map((pct) => (
      <motion.div
        key={pct}
        className="absolute top-0 bottom-0 w-px bg-[#F59E0B]/4"
        style={{ left: `${pct}%` }}
        animate={{ opacity: [0.2, 0.6, 0.2] }}
        transition={{ duration: 5 + pct / 30, repeat: Infinity, delay: pct / 60 }}
      />
    ))}
    <motion.div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-[#F59E0B]/5 rounded-full blur-[120px]"
      animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.8, 0.4] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

const AIInterviewTeaser = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const monoFont = { fontFamily: "'JetBrains Mono', monospace" };
  const soraFont = { fontFamily: "'Sora', sans-serif" };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitted(true);
  };

  return (
    <section style={{ backgroundColor: '#0A0F1E', ...soraFont }} className="relative py-28 md:py-36 overflow-hidden border-t border-[#1F2937]">
      <GridLines />

      {/* Dot texture */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #F59E0B 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 flex flex-col items-center text-center">

        {/* ── Launching Soon badge ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative mb-12"
        >
          <div style={{ borderColor: '#F59E0B33', backgroundColor: '#F59E0B11' }} className="relative inline-flex items-center gap-2.5 px-6 py-2 border">
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-2.5 h-2.5 bg-[#F59E0B]"
            />
            <span style={monoFont} className="text-[10px] font-black text-[#F59E0B] uppercase tracking-[0.3em]">
              Launching Soon
            </span>
          </div>
        </motion.div>

        {/* ── Headline ── */}
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={monoFont}
          className="text-4xl sm:text-5xl md:text-7xl font-black text-white leading-[1] tracking-tighter mb-8 uppercase"
        >
          Ace Your Python <br />
          <span className="text-[#F59E0B] italic">Interview</span> with AI
        </motion.h2>

        {/* ── Subheadline ── */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={soraFont}
          className="text-[#6B7280] text-lg leading-relaxed max-w-xl mb-20"
        >
          Mock interviews, instant feedback, and company-specific question banks —
          powered by AI.
        </motion.p>

        {/* ── Feature hint cards ── */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-6 mb-20">
          {FEATURES.map((f, i) => (
            <Card key={i} style={{ padding: '2.5rem' }} className="group hover:border-[#F59E0B] transition-all">
              <div style={{ backgroundColor: `${f.accent}11`, borderColor: `${f.accent}22` }} className="w-14 h-14 border flex items-center justify-center mb-8 mx-auto">
                <f.icon style={{ color: f.accent }} className="w-6 h-6" />
              </div>
              <h3 style={monoFont} className="text-sm font-black text-white uppercase tracking-tight mb-4">
                {f.title}
              </h3>
              <p style={soraFont} className="text-[#6B7280] text-[10px] leading-relaxed">
                {f.desc}
              </p>
            </Card>
          ))}
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#F59E0B]/20 to-transparent mb-20" />

        {/* ── Email capture ── */}
        <div className="w-full max-w-lg">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-4"
            >
              <div style={{ backgroundColor: '#00E67611', borderColor: '#00E67633' }} className="w-16 h-16 border flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#00E676]" />
              </div>
              <p style={monoFont} className="text-sm font-black text-[#00E676] uppercase tracking-widest">
                You're on the list!
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-0 w-full">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ENTER EMAIL ADDRESS"
                required
                className="flex-1 h-16"
              />
              <Button
                type="submit"
                variant="amber"
                className="h-16 px-10 flex-shrink-0"
              >
                <Bell className="w-4 h-4 mr-3" />
                Notify Me
              </Button>
            </form>
          )}
          {!submitted && (
            <p style={monoFont} className="text-[#6B7280] text-[9px] uppercase tracking-[0.3em] mt-8 font-black">
              Be the first to know. Zero Spam. Real updates.
            </p>
          )}
        </div>

        {/* ── Bottom stat strip ── */}
        <div style={monoFont} className="mt-24 flex flex-wrap items-center justify-center gap-10 text-[#6B7280] text-[10px] font-black uppercase tracking-widest">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#F59E0B]" />
            340+ on waitlist
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#00E676]" />
            Q3 2025 launch
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-white" />
            Early access free
          </span>
        </div>

      </div>
    </section>
  );
};

export default AIInterviewTeaser;
